import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Download, Lock, CheckCircle2, FileText, Loader2, Mail, AlertTriangle, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { trackPurchase, identifyUser } from "../lib/tiktok";

// Google Ads — Performance Ads OS Purchase conversion.
const fireGoogleAdsPurchase = (transactionId) => {
  try {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "conversion", {
      send_to: "AW-18239642780/eSJQCMzv7r4cEJy5q_lD",
      value: 1.0,
      currency: "SAR",
      transaction_id: transactionId || "",
    });
  } catch (_) { /* analytics must never break the flow */ }
};

const FONT_AR = "'Tajawal','IBM Plex Sans Arabic',-apple-system,system-ui,sans-serif";
const FONT_DISPLAY = "'Tajawal','IBM Plex Sans Arabic',serif";

const ensureFonts = () => {
  if (document.getElementById("course-font")) return;
  const l = document.createElement("link");
  l.id = "course-font";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800;900&display=swap";
  document.head.appendChild(l);
};

export default function CourseDownload() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const csId = params.get("cs") || "";
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileErr, setReconcileErr] = useState("");
  const [err, setErr] = useState("");
  const purchaseFiredRef = useRef(false);

  const firePurchasePixels = (o) => {
    if (purchaseFiredRef.current) return;
    purchaseFiredRef.current = true;
    try { if (o?.email) identifyUser({ email: o.email, externalId: token }); } catch (_) { /* analytics never breaks the flow */ }
    try {
      if (csId && typeof window !== "undefined" && window.ttq?.track) {
        const fired = `__perfads_purchase_${csId}`;
        if (!window[fired]) {
          window[fired] = true;
          // SAR-priced product; TikTok benchmark expects USD so we send a nominal USD value
          // matching the backend S2S Purchase event so TikTok dedupes via event_id.
          const usdValue = Math.round((order?.amount || 99) * 0.27 * 100) / 100;
          window.ttq.track("Purchase", {
            contents: [{ content_id: "perf_ads_os", content_type: "product", content_name: "Performance Ads OS", price: usdValue, quantity: 1 }],
            value: usdValue,
            currency: "USD",
          }, { event_id: `purchase_${csId}` });
        }
      } else if (csId) {
        // fallback to shared helper if ttq init differs
        trackPurchase(csId, "perf_ads_os");
      }
    } catch (_) { /* analytics never breaks the flow */ }
    fireGoogleAdsPurchase(csId || token);
  };

  const manualReconcile = async () => {
    setReconcileErr("");
    setReconciling(true);
    try {
      const { data } = await api.post(`/course/reconcile/${token}`);
      if (data?.status === "paid") {
        try {
          const { data: o } = await api.get(`/course/order/${token}`);
          setOrder(o);
          firePurchasePixels(o);
        } catch (_) { /* swallow */ }
      } else if (data?.reason) {
        setReconcileErr(data.reason);
      } else {
        setReconcileErr("الدفع لا يزال قيد المعالجة. حاول مرة أخرى بعد 10-20 ثانية.");
      }
    } catch (e) {
      setReconcileErr(e?.response?.data?.detail || "تعذّر الاتصال بـ Stripe — حاول مرة أخرى.");
    } finally {
      setReconciling(false);
    }
  };

  useEffect(() => {
    ensureFonts();
    document.title = "Performance Ads OS — تم استلام طلبك";
    let cancelled = false;
    let attempts = 0;
    let reconciled = false;

    const tryReconcile = async () => {
      if (reconciled) return false;
      reconciled = true;
      try {
        const { data } = await api.post(`/course/reconcile/${token}`);
        if (data?.status === "paid") return true;
      } catch (_) { /* swallow */ }
      return false;
    };

    const poll = async () => {
      while (!cancelled && attempts < 24) {
        attempts += 1;
        try {
          const { data } = await api.get(`/course/order/${token}`);
          if (cancelled) return;
          setOrder(data);
          if (data.status === "paid") {
            setLoading(false);
            firePurchasePixels(data);
            return;
          }
        } catch (e) {
          if (cancelled) return;
          if (e?.response?.status === 404) {
            setErr("هذا الرابط غير صالح أو منتهي الصلاحية.");
            setLoading(false);
            return;
          }
        }
        if (attempts === 4) {
          const flipped = await tryReconcile();
          if (flipped && !cancelled) continue;
        }
        await new Promise((r) => setTimeout(r, attempts < 4 ? 1500 : 3000));
      }
      if (!cancelled && !reconciled) {
        await tryReconcile();
        try {
          const { data } = await api.get(`/course/order/${token}`);
          if (!cancelled) setOrder(data);
          if (!cancelled && data?.status === "paid") {
            firePurchasePixels(data);
          }
        } catch (_) { /* swallow */ }
      }
      if (!cancelled) setLoading(false);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, csId]);

  return (
    <div
      dir="rtl"
      lang="ar"
      className="min-h-screen bg-[#070C18] text-[#F0EAD9] selection:bg-[#C9A66A]/40 flex flex-col"
      style={{ fontFamily: FONT_AR }}
      data-testid="course-download-root"
    >
      <nav className="px-5 pt-5 max-w-5xl w-full mx-auto flex items-center justify-between">
        <Link to="/perfads" className="flex items-center gap-2.5" data-testid="course-download-logo">
          <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#C9A66A] to-[#8A6E3D] flex items-center justify-center font-extrabold text-[#0A0F1C] text-[14px]">P</span>
          <div className="leading-tight">
            <div className="font-extrabold text-[14px] tracking-tight">Performance Ads OS</div>
            <div className="text-[10px] text-[#A7956E]">نظام تشغيل الإعلانات المدفوعة</div>
          </div>
        </Link>
        <span className="text-[11px] text-[#A7956E] flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5 text-[#C9A66A]" /> وصول خاص
        </span>
      </nav>

      <main className="flex-1 px-5 py-12 sm:py-20 flex items-center justify-center">
        <div className="max-w-xl w-full">
          {loading && (
            <div className="text-center py-12" data-testid="course-download-loading">
              <Loader2 className="w-7 h-7 text-[#C9A66A] animate-spin mx-auto mb-4" />
              <div className="text-[15px] text-white font-semibold">جاري التحقق من الدفع…</div>
              <div className="text-[12.5px] text-[#A7956E] mt-1.5">عادة لا يستغرق سوى بضع ثوانٍ.</div>
            </div>
          )}

          {!loading && order?.status === "paid" && (
            <div data-testid="course-download-success">
              <div className="text-center mb-9">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C9A66A]/[0.15] border border-[#C9A66A]/40 mb-5">
                  <CheckCircle2 className="w-7 h-7 text-[#C9A66A]" strokeWidth={1.8} />
                </div>
                <div className="text-[10.5px] tracking-[0.28em] text-[#C9A66A] font-bold mb-3">تم تأكيد الدفع</div>
                <h1 className="font-bold text-[30px] sm:text-[40px] leading-[1.15] tracking-tight text-white" style={{ fontFamily: FONT_DISPLAY, fontWeight: 800 }}>
                  وصلك Performance Ads OS.
                </h1>
                <p className="mt-4 text-[14.5px] text-[#CFC3A1] leading-[1.85]">
                  شكرًا لإتمامك الشراء. أرسلنا نسخة من رابط الوصول إلى <span className="text-white font-semibold">{order.email || "بريدك المسجّل في Stripe"}</span>.
                </p>
              </div>

              <div className="bg-gradient-to-b from-[#15203A] to-[#0E162B] border border-[#C9A66A]/45 rounded-2xl p-6 shadow-[0_30px_80px_-25px_rgba(201,166,106,0.45)]" data-testid="course-download-card">
                <div className="flex items-center gap-4">
                  <span className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#C9A66A] to-[#8A6E3D] flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-[#0A0F1C]" strokeWidth={2} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.24em] text-[#A7956E] font-bold">PERFORMANCE ADS OS · MVP</div>
                    <div className="text-[15px] font-bold text-white truncate">6 دروس + Workbook + قوالب قرار</div>
                    <div className="text-[11.5px] text-[#A7956E] mt-0.5">{(order.amount || 99).toFixed(0)} {order.currency || "SAR"} · دفعة واحدة</div>
                  </div>
                </div>

                <a
                  href={order.download_url || "https://mega.nz/folder/J7VmiJQT#2vtlVxVVY-G8YSwmIzhM4A"}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="course-download-btn"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-b from-[#E2C281] to-[#C9A66A] hover:from-[#EBCD93] hover:to-[#D6B477] text-[#0A0F1C] font-extrabold text-[15px] tracking-wide px-7 py-4 rounded-full transition-all min-h-[54px] shadow-[0_10px_36px_-10px_rgba(201,166,106,0.65)]"
                >
                  <Download className="w-4 h-4" /> تحميل المحتوى الكامل
                </a>
                <div className="mt-3 text-[11.5px] text-[#A7956E] flex items-center justify-center gap-1.5">
                  <Lock className="w-3 h-3 text-[#C9A66A]" /> الرابط يفتح مجلد MEGA يحوي 6 دروس + Workbook + قوالب القرار.
                </div>

                <div className="mt-5 bg-[#0A0F1C]/60 border border-[#C9A66A]/25 rounded-xl p-4 text-right" data-testid="course-access-note">
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-[#C9A66A] shrink-0 mt-0.5" />
                    <div className="text-[13px] text-[#E7DDC0] leading-[1.85]">
                      أرسلنا نفس الرابط على بريدك الإلكتروني للوصول السريع لاحقًا. إذا لم يصلك خلال دقائق، تحقّق من Spam أو راسلنا.
                    </div>
                  </div>
                </div>

                <Link
                  to="/perfads"
                  data-testid="course-back-btn"
                  className="mt-5 inline-flex items-center justify-center gap-2 w-full text-[13px] text-[#A7956E] hover:text-[#C9A66A] transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 rotate-180" /> العودة إلى الصفحة الرئيسية
                </Link>
              </div>

              <div className="mt-8 text-[12.5px] text-[#A7956E] leading-relaxed text-center">
                لم يصلك البريد الإلكتروني؟ تواصل معنا وسنرسله مجددًا.
                <div className="mt-1.5 inline-flex items-center gap-1.5 text-[#CFC3A1]">
                  <Mail className="w-3.5 h-3.5" /> support@replyrescue.app
                </div>
              </div>
            </div>
          )}

          {!loading && order && order.status !== "paid" && (
            <div className="text-center py-10" data-testid="course-download-pending">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#FBBF24]/[0.12] border border-[#FBBF24]/40 mb-5">
                <Loader2 className="w-6 h-6 text-[#FDE68A] animate-spin" />
              </div>
              <h1 className="text-[22px] font-bold text-white">جاري إتمام عملية الدفع…</h1>
              <p className="mt-3 text-[13.5px] text-[#CFC3A1] leading-relaxed max-w-md mx-auto">
                Stripe لا يزال يُنهي تأكيد العملية. سنتحقق منه مباشرة — عادة لا يستغرق سوى ثوانٍ.
              </p>
              <button
                onClick={manualReconcile}
                disabled={reconciling}
                data-testid="course-download-reconcile"
                className="mt-6 inline-flex items-center gap-2 bg-gradient-to-b from-[#E2C281] to-[#C9A66A] text-[#0A0F1C] font-extrabold text-[13.5px] tracking-wide px-6 py-3 rounded-full transition-all min-h-[46px] disabled:opacity-60"
              >
                {reconciling ? <><Loader2 className="w-4 h-4 animate-spin" /> جاري التحقق من Stripe…</> : "تحقّق من Stripe الآن"}
              </button>
              {reconcileErr && (
                <div className="mt-4 text-[12.5px] text-[#F0838F] max-w-md mx-auto" data-testid="course-reconcile-err">
                  {reconcileErr}
                </div>
              )}
              <div className="mt-3 text-[11.5px] text-[#A7956E]">
                تم خصم المبلغ ولا يزال الطلب معلقًا؟ راسل <a href="mailto:support@replyrescue.app" className="text-[#C9A66A] hover:underline">support@replyrescue.app</a> مع إيصال الدفع.
              </div>
            </div>
          )}

          {!loading && err && !order && (
            <div className="text-center py-10" data-testid="course-download-error">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#F87171]/[0.12] border border-[#F87171]/40 mb-5">
                <AlertTriangle className="w-6 h-6 text-[#FCA5A5]" />
              </div>
              <h1 className="text-[22px] font-bold text-white">الرابط غير موجود</h1>
              <p className="mt-3 text-[13.5px] text-[#CFC3A1] leading-relaxed max-w-md mx-auto">{err}</p>
              <Link
                to="/perfads"
                data-testid="course-download-back"
                className="mt-6 inline-flex items-center gap-2 bg-gradient-to-b from-[#E2C281] to-[#C9A66A] text-[#0A0F1C] font-extrabold text-[13.5px] tracking-wide px-6 py-3 rounded-full transition-all min-h-[46px]"
              >
                العودة إلى Performance Ads OS <ArrowLeft className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </main>

      <footer className="px-5 py-7 border-t border-white/[0.06] text-center">
        <p className="text-[11px] text-[#7A6F50] max-w-lg mx-auto leading-relaxed">
          Performance Ads OS — منتج تعليمي مستقل. لا يضمن المنتج تحقيق نتائج إعلانية محددة.
        </p>
      </footer>
    </div>
  );
}
